import { Text } from "@/components/podkit/typography/Text";
import { type PlainWorkflow, useGetMostRecentExecution } from "@/queries/automation-queries";
import { workflowName, isWorkflowDeleting } from "@/utils/automations";
import { type FC, useState } from "react";
import { WorkflowExecutionStatusIcon } from "@/components/automations/AutomationExecutionStatusIcon";
import { WorkflowExecutionStatusText } from "@/components/automations/AutomationExecutionStatusText";
import { AutomationPropertiesHeader } from "@/routes/automations/AutomationPropertiesHeader";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown.tsx";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { SubjectDisplay } from "@/components/SubjectDisplay";
import { AutomationGroupAccess } from "@/components/automations/AutomationGroupAccess";

type WorkflowCardProps = {
    workflow: PlainWorkflow;
    readonly?: boolean;
} & PropsWithClassName;

export const AutomationCard: FC<WorkflowCardProps> = ({ workflow, className, readonly = false }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { data: mostRecentExecution } = useGetMostRecentExecution({
        enabled: true,
        workflowId: workflow.id,
    });

    const hasDescription = !!workflow.metadata?.description;
    const creator = workflow.metadata?.creator;
    const isDeleting = isWorkflowDeleting(workflow);
    const showActionsMenu = !readonly;

    return (
        <div
            className={cn(
                "flex flex-col gap-3 rounded-xl border border-border-base p-4",
                { "shadow-sm": isDropdownOpen },
                { "hover:shadow-sm": !readonly },
                className,
            )}
        >
            <div>
                <div className="flex items-center justify-between gap-2">
                    <Text className="text-base font-medium text-content-primary">{workflowName(workflow)}</Text>
                    <div className="flex items-center gap-1">
                        <AutomationGroupAccess workflowId={workflow.id} maxVisible={3} />
                        {showActionsMenu && (
                            <WorkflowActionsDropdown
                                workflow={workflow}
                                showRunAction={true}
                                buttonSize={"sm"}
                                onOpenChange={setIsDropdownOpen}
                            />
                        )}
                    </div>
                </div>
                {hasDescription ? (
                    <Text className="text-base text-content-strong">{workflow.metadata?.description}</Text>
                ) : (
                    <div className="mt-2">
                        <AutomationPropertiesHeader workflow={workflow} editable={false} />
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1">
                <SubjectDisplay subject={creator} />
                <span className="text-content-muted">·</span>
                {isDeleting ? (
                    <>
                        <IconSpinner size="sm" className="shrink-0 animate-spin text-content-muted" />
                        <Text className="text-sm text-content-muted">Deleting</Text>
                    </>
                ) : mostRecentExecution ? (
                    <>
                        <WorkflowExecutionStatusIcon execution={mostRecentExecution} />
                        <WorkflowExecutionStatusText execution={mostRecentExecution} />
                    </>
                ) : (
                    <Text className="text-sm text-content-muted">No executions yet</Text>
                )}
            </div>
        </div>
    );
};
